import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  F0ActionBar,
  F0AnalyticsDashboard,
  F0Box,
  type ChatDashboardChartConfig,
  type ChatDashboardChartItem,
  type ChatDashboardItem,
  type ChatDashboardMetricItem,
  type FormatPreset,
} from '@factorialco/f0-react'
import { Save } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'
import { toF0ChatDashboardConfig } from 'modules/ai/copilotActions/canvas/dashboards/f0Adapters'

import { useDashboardCanvas } from './DashboardProvider'
import type { DashboardItemLayout } from './DashboardProvider'
import type {
  DashboardCanvasContent,
  DashboardCollectionItem as DashboardRuntimeCollectionItem,
  DashboardDerivedFilters,
  DashboardFilterOptions,
  DashboardRuntimeConfig,
} from './types'
import { useDashboardCompute, type ItemResult } from './useDashboardCompute'
import { savedDashboardMetaStore } from './configStore'
import { SaveDashboardDialog } from './SaveDashboardDialog'
import {
  buildDashboardFilterKeySets,
  buildFilterDefinitions,
  buildNavigationFilterDefinitions,
  serializeDashboardFilters,
} from './filters'

// Internal F0AnalyticsDashboard types are not exported publicly. We use
// relaxed aliases at the boundaries — the mappers return objects shaped
// like the underlying types and F0AnalyticsDashboard accepts them as items.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DashboardChartConfig = any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DashboardChartItem = any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DashboardCollectionItem = any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DashboardMetricItem = any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DashboardItem = any
type FiltersState<_T = unknown> = Record<string, unknown>
type RecordType = Record<string, unknown>

// ---------------------------------------------------------------------------
// Minimum item height per type
// ---------------------------------------------------------------------------

/**
 * Minimum item height (in pixels) enforced for every dashboard item received
 * from the agent, regardless of what config it sends. Mirrors the
 * `ROW_HEIGHTS` defaults defined in F0AnalyticsDashboard's `DashboardGrid`
 * (chart 336px, metric 144px, collection 480px). Keep both in sync.
 */
const MIN_ITEM_HEIGHT_BY_TYPE: Record<ChatDashboardItem['type'], number> = {
  chart: 336,
  metric: 144,
  collection: 480,
}

/**
 * Resolve an item's height in pixels using the same precedence as the F0
 * grid: `itemHeight` (canonical) > `rowSpan * 48` (legacy) > 0 (no value).
 */
function readItemHeightPx(item: ChatDashboardItem): number {
  if (item.itemHeight && item.itemHeight > 0) return item.itemHeight
  if (item.rowSpan && item.rowSpan > 0) return item.rowSpan * 48
  return 0
}

/**
 * Returns the item with its `itemHeight` clamped to the per-type minimum.
 *
 * Backwards compat: when the agent sends only the legacy `rowSpan`, the
 * function reads it as a fallback and writes the result into `itemHeight`
 * so the grid uses the canonical pixel field downstream.
 */
export function clampDashboardItemRowSpan(item: ChatDashboardItem): ChatDashboardItem {
  const min = MIN_ITEM_HEIGHT_BY_TYPE[item.type] ?? 0
  const current = readItemHeightPx(item)
  const next = Math.max(min, current)
  if (current >= min && item.itemHeight && item.itemHeight === next) return item
  return { ...item, itemHeight: next }
}

/**
 * True when the dashboard is effectively a table — a single item of type
 * `collection`.
 */
export function isSingleCollectionDashboard(
  config: Pick<DashboardRuntimeConfig, 'items'>
): boolean {
  return config.items.length === 1 && config.items[0]?.type === 'collection'
}

// ---------------------------------------------------------------------------
// Format preset → formatter function
// ---------------------------------------------------------------------------

function buildFormatter(preset?: FormatPreset): ((value: number) => string) | undefined {
  if (!preset) return undefined

  switch (preset.type) {
    case 'number':
      return (v) => v.toLocaleString()

    case 'currency': {
      const currency = preset.currency ?? 'EUR'
      return (v) => {
        try {
          return v.toLocaleString(undefined, {
            style: 'currency',
            currency,
            maximumFractionDigits: 0,
          })
        } catch {
          return `${v}`
        }
      }
    }

    case 'percent':
      return (v) => `${v}%`

    case 'compact':
      return (v) => {
        if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`
        if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(1)}k`
        return `${v}`
      }

    default:
      return undefined
  }
}

// ---------------------------------------------------------------------------
// Chat chart config → Dashboard chart config mapper
// ---------------------------------------------------------------------------

function toDashboardChartConfig(chatChart: ChatDashboardChartConfig): DashboardChartConfig {
  const valueFormatter = buildFormatter(
    'valueFormat' in chatChart ? chatChart.valueFormat : undefined
  )

  switch (chatChart.type) {
    case 'bar': {
      const { valueFormat: _, ...rest } = chatChart
      return { ...rest, ...(valueFormatter ? { valueFormatter } : {}) }
    }
    case 'line': {
      const { valueFormat: _, ...rest } = chatChart
      return { ...rest, ...(valueFormatter ? { valueFormatter } : {}) }
    }
    case 'funnel': {
      const { valueFormat: _, ...rest } = chatChart
      return { ...rest, ...(valueFormatter ? { valueFormatter } : {}) }
    }
    case 'radar': {
      const { valueFormat: _, ...rest } = chatChart
      return { ...rest, ...(valueFormatter ? { valueFormatter } : {}) }
    }
    case 'pie': {
      const { valueFormat: _, ...rest } = chatChart
      return { ...rest, ...(valueFormatter ? { valueFormatter } : {}) }
    }
    case 'gauge': {
      const { valueFormat: _, ...rest } = chatChart
      return { ...rest, ...(valueFormatter ? { valueFormatter } : {}) }
    }
    case 'heatmap': {
      const { valueFormat: _, ...rest } = chatChart
      return { ...rest, ...(valueFormatter ? { valueFormatter } : {}) }
    }
  }
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const COLLECTION_PER_PAGE = 20
const CHART_TYPES: ReadonlySet<string> = new Set<ChatDashboardChartConfig['type']>([
  'bar',
  'funnel',
  'gauge',
  'heatmap',
  'line',
  'pie',
  'radar',
])

function isChartType(value: string): value is ChatDashboardChartConfig['type'] {
  return CHART_TYPES.has(value)
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface ChatDashboardProps {
  config: DashboardRuntimeConfig
  apiConfig: {
    baseUrl: string
    headers: Record<string, string>
    runtimeFetch?: typeof fetch
  }
  refreshKey?: number
  /** Incrementing counter that forces the grid to reset to initial layout */
  resetKey?: number
  editMode?: boolean
  /** Called when a chart item's type is changed (e.g. bar → line) */
  onTransformChart?: (
    itemId: string,
    newType: string,
    orientation?: 'vertical' | 'horizontal'
  ) => void
  onLayoutChange?: (layout: DashboardItemLayout[]) => void
  onExportReady?: (exportFn: (() => Promise<void>) | undefined) => void
  exportFilename?: string
  onSnapshotDateChange?: (isoDate: string | null) => void
}

/**
 * Renders an F0AnalyticsDashboard from a server-computed config.
 */
export function ChatDashboard({
  config,
  apiConfig,
  refreshKey = 0,
  resetKey,
  editMode,
  onLayoutChange,
  onTransformChart,
  onExportReady,
  exportFilename,
  onSnapshotDateChange,
}: ChatDashboardProps) {
  const { fetchItem, getFilterOptions, getDerivedFilters, getHasResponse } = useDashboardCompute(
    config,
    apiConfig,
    refreshKey
  )

  const [filterOptions, setFilterOptions] = useState<DashboardFilterOptions | undefined>()
  const [derivedFilters, setDerivedFilters] = useState<DashboardDerivedFilters>({})
  const [filterRevision, setFilterRevision] = useState(0)
  const [firstComputeSettled, setFirstComputeSettled] = useState(false)
  const firstComputeSettledRef = useRef(false)
  const syncedFilterOptionsRef = useRef<DashboardFilterOptions | undefined>()
  const syncedDerivedFiltersRef = useRef<DashboardDerivedFilters>({})

  const markFirstComputeSettled = useCallback(() => {
    if (firstComputeSettledRef.current) return
    firstComputeSettledRef.current = true
    setFilterRevision((n) => n + 1)
    setFirstComputeSettled(true)
  }, [])

  const syncLatestComputeResponse = useCallback(() => {
    if (!getHasResponse()) return

    const opts = getFilterOptions()
    const derived = getDerivedFilters()
    if (syncedFilterOptionsRef.current !== opts) {
      syncedFilterOptionsRef.current = opts
      setFilterOptions(opts)
    }
    if (
      syncedDerivedFiltersRef.current.filters !== derived.filters ||
      syncedDerivedFiltersRef.current.navigationFilters !== derived.navigationFilters
    ) {
      syncedDerivedFiltersRef.current = derived
      setDerivedFilters(derived)
    }
    markFirstComputeSettled()
  }, [getDerivedFilters, getFilterOptions, getHasResponse, markFirstComputeSettled])

  useEffect(() => {
    firstComputeSettledRef.current = false
    syncedFilterOptionsRef.current = undefined
    syncedDerivedFiltersRef.current = {}
    setFirstComputeSettled(false)
    setFilterOptions(undefined)
    setDerivedFilters({})
  }, [refreshKey])

  const expectsFilterControls = useMemo(
    () =>
      config.snapshot === true ||
      Boolean(config.dateNavigatorColumn) ||
      Object.keys(config.filters ?? {}).length > 0 ||
      Object.keys(config.navigationFilters ?? {}).length > 0,
    [config.dateNavigatorColumn, config.filters, config.navigationFilters, config.snapshot]
  )

  const filtersLoading = expectsFilterControls && !firstComputeSettled

  useEffect(() => {
    if (firstComputeSettled || !expectsFilterControls) return
    if (config.items.length === 0) {
      markFirstComputeSettled()
      return
    }
    // F0 owns the first fetchData promise, so poll until the compute response
    // lands and this component can sync derived filters into React state.
    const interval = setInterval(() => {
      if (getHasResponse()) {
        syncLatestComputeResponse()
        clearInterval(interval)
      }
    }, 100)
    return () => clearInterval(interval)
  }, [
    config.items.length,
    expectsFilterControls,
    firstComputeSettled,
    getHasResponse,
    markFirstComputeSettled,
    syncLatestComputeResponse,
  ])

  const filterDefinitions = useMemo(
    () => buildFilterDefinitions(derivedFilters.filters, filterOptions),
    [derivedFilters.filters, filterOptions]
  )

  const dashboardNavigationFilters = useMemo(
    () =>
      buildNavigationFilterDefinitions(derivedFilters.navigationFilters, {
        snapshot: config.snapshot,
        snapshotDate: config.snapshotDate,
      }),
    [derivedFilters.navigationFilters, config.snapshot, config.snapshotDate]
  )

  const { navigationFilterKeys, numericRangeFilterKeys, dateFilterKeys } = useMemo(
    () => buildDashboardFilterKeySets(derivedFilters, { snapshot: config.snapshot }),
    [derivedFilters.filters, derivedFilters.navigationFilters, config.snapshot]
  )

  const makeFetchData = useCallback(
    (itemId: string) => {
      return (filters: FiltersState): Promise<ItemResult> => {
        const { filterValues, navigationFilterValues } = serializeDashboardFilters({
          filters,
          navigationFilterKeys,
          numericRangeFilterKeys,
          dateFilterKeys,
          snapshotDate: config.snapshotDate,
          onSnapshotDateChange,
        })

        return fetchItem(itemId, filterValues, navigationFilterValues)
          .then((result) => {
            syncLatestComputeResponse()
            return result
          })
          .catch((error: unknown) => {
            markFirstComputeSettled()
            throw error
          })
      }
    },
    [
      config.snapshotDate,
      dateFilterKeys,
      fetchItem,
      markFirstComputeSettled,
      navigationFilterKeys,
      numericRangeFilterKeys,
      onSnapshotDateChange,
      syncLatestComputeResponse,
    ]
  )

  const items: DashboardItem[] = useMemo(
    () =>
      config.items.map((item) => {
        switch (item.type) {
          case 'chart':
            return mapChartItem(item, makeFetchData(item.id))
          case 'metric':
            return mapMetricItem(item, makeFetchData(item.id))
          case 'collection':
            return mapCollectionItem(item, makeFetchData(item.id))
        }
      }),
    [config.items, makeFetchData]
  )

  return (
    <F0AnalyticsDashboard
      key={`${refreshKey}:${filterRevision}:${resetKey ?? 0}`}
      filters={filterDefinitions}
      filtersLoading={filtersLoading}
      navigationFilters={dashboardNavigationFilters}
      items={items}
      editMode={editMode}
      onLayoutChange={onLayoutChange}
      onTransformChart={onTransformChart}
      onExportReady={onExportReady}
      exportFilename={exportFilename}
      resetKey={resetKey}
    />
  )
}

ChatDashboard.displayName = 'ChatDashboard'

// ---------------------------------------------------------------------------
// Canvas content wrapper — bridges canvas context to ChatDashboard props
// ---------------------------------------------------------------------------

export type DashboardContentProps = {
  content: DashboardCanvasContent
  refreshKey: number
}

export function DashboardContent({
  content,
  refreshKey: _parentRefreshKey,
}: DashboardContentProps): ReactNode {
  const {
    isDirty,
    discardKey,
    itemTransforms,
    onLayoutChange,
    handleSave,
    handleDiscard,
    clearPendingChanges,
    transformItem,
    saveConfigToHistory,
    setPendingSnapshotDate,
    buildPendingConfig,
    registerExport,
    canvasActions,
    onCanvasContentChange,
  } = useDashboardCanvas()
  const [isSaveAsDialogOpen, setIsSaveAsDialogOpen] = useState(false)

  // Treat a dashboard as "saved" only when both id AND category are present.
  const isSavedDashboard = !!content.savedDashboardId && !!content.savedDashboardCategory
  const isUnsaved = !!content.savedDashboardUnsaved
  const hasDashboardActions = !!canvasActions?.dashboard

  const effectiveConfig = useMemo(() => {
    return {
      ...content.config,
      items: content.config.items.map((item) => {
        const patch = itemTransforms.get(item.id)
        const merged = patch ? Object.assign({}, item, patch) : item
        return clampDashboardItemRowSpan(merged)
      }),
    }
  }, [content.config, itemTransforms])

  const handleSaveAs = useCallback(
    async (title: string, description: string) => {
      const configToSave = buildPendingConfig()
      if (!configToSave) return

      const created = await canvasActions?.dashboard?.create(
        title,
        description,
        toF0ChatDashboardConfig(configToSave),
        content.savedDashboardCategory
      )
      if (created) {
        const meta = {
          savedDashboardId: created.id,
          savedDashboardCategory: created.category,
          savedDashboardDescription: description,
          savedDashboardUnsaved: false,
        }

        onCanvasContentChange({ ...content, config: configToSave, ...meta })

        if (content.toolCallId) {
          savedDashboardMetaStore.set(content.toolCallId, meta)
        }

        void saveConfigToHistory({ ...configToSave, ...meta })
        clearPendingChanges()
      }
    },
    [
      buildPendingConfig,
      canvasActions,
      clearPendingChanges,
      content,
      onCanvasContentChange,
      saveConfigToHistory,
    ]
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dataRefreshKey = useMemo(() => Date.now(), [content.config.fetchSpecs])

  return (
    <F0Box display="flex" height="full" flexDirection="column">
      <F0Box display="flex" flexDirection="column" grow minHeight="0" overflow="auto">
        <ChatDashboard
          config={effectiveConfig}
          apiConfig={content.apiConfig}
          refreshKey={dataRefreshKey}
          resetKey={discardKey}
          editMode
          onLayoutChange={onLayoutChange}
          onTransformChart={(itemId, newType, orientation) => {
            const item = effectiveConfig.items.find((i) => i.id === itemId)
            if (!item || item.type !== 'chart' || !isChartType(newType)) return
            const updatedChart = {
              ...item.chart,
              type: newType,
              ...(newType === 'bar' ? { orientation: orientation ?? 'vertical' } : {}),
            }
            transformItem(itemId, { chart: updatedChart })
          }}
          onExportReady={registerExport}
          exportFilename={content.title}
          onSnapshotDateChange={setPendingSnapshotDate}
        />
      </F0Box>
      {/* State A: New dashboard with canvasActions */}
      {!isSavedDashboard && hasDashboardActions && (
        <F0ActionBar
          label={
            isSingleCollectionDashboard(effectiveConfig)
              ? i18n.t('common.ai.dashboard.save_table_to_analytics')
              : i18n.t('common.ai.dashboard.save_to_analytics')
          }
          isOpen
          primaryActions={[
            {
              label: i18n.t('common.ai.dashboard.save'),
              onClick: () => setIsSaveAsDialogOpen(true),
              icon: Save,
            },
          ]}
          secondaryActions={
            isDirty
              ? [
                  {
                    label: i18n.t('common.ai.discard_changes'),
                    onClick: handleDiscard,
                  },
                ]
              : undefined
          }
        />
      )}
      {/* State B1: Saved dashboard, user edited layout */}
      {isSavedDashboard && hasDashboardActions && isDirty && (
        <F0ActionBar
          label={i18n.t('common.ai.unsaved_changes')}
          isOpen
          primaryActions={[
            {
              label: i18n.t('common.ai.dashboard.save'),
              onClick: handleSave,
              icon: Save,
            },
          ]}
          secondaryActions={[
            {
              label: i18n.t('common.ai.save_as_changes'),
              onClick: () => setIsSaveAsDialogOpen(true),
            },
            {
              label: i18n.t('common.ai.discard_changes'),
              onClick: handleDiscard,
            },
          ]}
        />
      )}
      {/* State B2: Saved dashboard, agent iterated (unsaved) but no manual edits */}
      {isSavedDashboard && hasDashboardActions && !isDirty && isUnsaved && (
        <F0ActionBar
          label={i18n.t('common.ai.unsaved_changes')}
          isOpen
          primaryActions={[
            {
              label: i18n.t('common.ai.dashboard.save'),
              onClick: handleSave,
              icon: Save,
            },
          ]}
          secondaryActions={[
            {
              label: i18n.t('common.ai.save_as_changes'),
              onClick: () => setIsSaveAsDialogOpen(true),
            },
          ]}
        />
      )}
      {/* State C: No canvasActions */}
      {!hasDashboardActions && (
        <F0ActionBar
          label={i18n.t('common.ai.unsaved_changes')}
          isOpen={isDirty}
          primaryActions={[
            {
              label: i18n.t('common.ai.save_changes'),
              onClick: handleSave,
              icon: Save,
            },
          ]}
          secondaryActions={[
            {
              label: i18n.t('common.ai.discard_changes'),
              onClick: handleDiscard,
            },
          ]}
        />
      )}
      {hasDashboardActions && (
        <SaveDashboardDialog
          isOpen={isSaveAsDialogOpen}
          onClose={() => setIsSaveAsDialogOpen(false)}
          onSave={handleSaveAs}
          defaultTitle={content.title}
          defaultDescription={content.savedDashboardDescription ?? content.config.description}
        />
      )}
    </F0Box>
  )
}

// ---------------------------------------------------------------------------
// Item mappers
// ---------------------------------------------------------------------------

function mapChartItem(
  item: ChatDashboardChartItem,
  fetchData: (filters: FiltersState) => Promise<ItemResult>
): DashboardChartItem {
  return {
    id: item.id,
    title: item.title,
    description: buildDescription(item.description, item.sourceDescription),
    explanation: item.explanation,
    colSpan: item.colSpan,
    rowSpan: item.rowSpan,
    itemHeight: item.itemHeight,
    x: item.x,
    y: item.y,
    type: 'chart',
    chart: toDashboardChartConfig(item.chart),
    fetchData: (filters: FiltersState) =>
      fetchData(filters).then((r) => r.chart ?? { categories: [], series: [] }),
  }
}

function buildDescription(description?: string, sourceDescription?: string): string | undefined {
  if (sourceDescription) {
    return description ? `${description}\n${sourceDescription}` : sourceDescription
  }
  return description
}

function readColumnInfo(
  column: DashboardRuntimeCollectionItem['columns'][number]
): string | undefined {
  if ('info' in column && typeof column.info === 'string') return column.info
  return undefined
}

function mapMetricItem(
  item: ChatDashboardMetricItem,
  fetchData: (filters: FiltersState) => Promise<ItemResult>
): DashboardMetricItem {
  return {
    id: item.id,
    title: item.title,
    description: buildDescription(item.description, item.sourceDescription),
    explanation: item.explanation,
    colSpan: item.colSpan,
    rowSpan: item.rowSpan,
    itemHeight: item.itemHeight,
    x: item.x,
    y: item.y,
    type: 'metric',
    format: item.format,
    decimals: item.decimals,
    fetchData: (filters: FiltersState) => fetchData(filters).then((r) => r.metric ?? { value: 0 }),
  }
}

function mapCollectionItem(
  item: DashboardRuntimeCollectionItem,
  fetchData: (filters: FiltersState) => Promise<ItemResult>
): DashboardCollectionItem {
  const sortingDefs = item.columns.reduce<Record<string, { label: string }>>((acc, col) => {
    acc[col.id] = { label: col.label }
    return acc
  }, {})

  const applySortings = (
    rows: RecordType[],
    sortings: Array<{ field: string; order: 'asc' | 'desc' }> | undefined
  ): RecordType[] => {
    if (!sortings || sortings.length === 0) return rows
    const sorted = [...rows]
    sorted.sort((a, b) => {
      for (const { field, order } of sortings) {
        const av = a[field]
        const bv = b[field]
        if (av === bv) continue
        if (av == null) return order === 'asc' ? -1 : 1
        if (bv == null) return order === 'asc' ? 1 : -1
        let cmp: number
        if (typeof av === 'number' && typeof bv === 'number') {
          cmp = av - bv
        } else {
          cmp = String(av).localeCompare(String(bv), undefined, {
            numeric: true,
            sensitivity: 'base',
          })
        }
        if (cmp !== 0) return order === 'asc' ? cmp : -cmp
      }
      return 0
    })
    return sorted
  }

  return {
    id: item.id,
    title: item.title,
    description: buildDescription(item.description, item.sourceDescription),
    explanation: item.explanation,
    colSpan: item.colSpan ?? 12,
    rowSpan: item.rowSpan,
    itemHeight: item.itemHeight,
    x: item.x,
    y: item.y,
    type: 'collection',
    createSource: (filters: FiltersState) => {
      let cachedRows: RecordType[] | null = null
      const dataPromise = fetchData(filters).then((r) => {
        cachedRows = r.collection?.rows ?? []
        return cachedRows
      })

      return {
        sortings: sortingDefs,
        dataAdapter: {
          paginationType: 'pages' as const,
          perPage: COLLECTION_PER_PAGE,
          fetchData: async ({
            sortings,
            pagination,
            search,
          }: {
            filters: Record<string, unknown>
            sortings?: Array<{ field: string; order: 'asc' | 'desc' }>
            pagination: { currentPage: number; perPage?: number }
            search?: string
          }) => {
            const allRows = cachedRows ?? (await dataPromise)

            let rows = allRows
            if (search) {
              const q = search.toLowerCase()
              rows = rows.filter((row) =>
                Object.values(row).some((v) => v != null && String(v).toLowerCase().includes(q))
              )
            }
            rows = applySortings(rows, sortings)
            const page = pagination?.currentPage ?? 1
            const perPage = pagination?.perPage ?? COLLECTION_PER_PAGE
            const start = (page - 1) * perPage
            const pageRecords = rows.slice(start, start + perPage)

            return {
              type: 'pages' as const,
              records: pageRecords,
              total: rows.length,
              currentPage: page,
              perPage,
              pagesCount: Math.ceil(rows.length / perPage),
            }
          },
        },
        search: {
          enabled: true,
          sync: true,
        },
      }
    },
    visualizations: [
      {
        type: 'table' as const,
        options: {
          allowColumnHiding: true,
          allowColumnReordering: true,
          frozenColumns: 1 as const,
          columns: item.columns.map((col) => {
            const info = readColumnInfo(col)
            return {
              label: col.label,
              id: col.id,
              sorting: col.id,
              ...(col.width ? { width: col.width } : {}),
              ...(info ? { info } : {}),
              render: (row: RecordType) => {
                const value = row[col.id]
                if (value == null) return '-'
                return String(value)
              },
            }
          }),
        },
      },
    ],
  }
}
